/* =====================================================================
   Free Gift Upsells — 自定义元素 <free-gift-upsells>

   职责：
   1. 勾选/取消勾选，按 data-free-count 计算哪几件免费（先勾选的先免）
   2. 选满上限后锁住未勾选的行（与 Lectric 的强制态一致）
   3. 接管主题商品表单的 submit，把主商品 + 已选赠品一次性加进购物车
   4. 未勾满 N 件时硬阻断加购（Shopify 规则：get 数量不足整条折扣不生效，
      不降级为「勾几件免几件」，所以必须在前端拦死，否则顾客会按原价买单）

   锁定态（data-locked="true"，Liquid 侧默认开启）：
   赠品行全部预勾选且按钮 disabled → 顾客点不动，勾选不可取消（对齐竞品 Lectric）。
   加价购行不受影响，仍可自由勾选；两种行的颜色下拉在锁定态下都保持可用。
   锁定态下 checkedCount 恒等于 max，第 4 条硬阻断自然走不到。

   注意：真正的 $0 由后台 Buy X get Y 自动折扣生效，前端只做展示与加购。

   依赖事实（Motion 11.0.0）：
   - theme.js:2772 是 form 元素上的 bubble 阶段 submit 监听
     → document capture 阶段 stopImmediatePropagation() 能抢在它前面
   - 我们的 addWithGifts 必须自己派发 'ajaxProduct:added'
     因为 theme.js 的 addItemFromForm 被跳过后，它的 success() 不会执行，
     购物车抽屉不会自动打开（theme.js:4386 的 CartDrawer 依赖该事件）
   ===================================================================== */

(function () {
  'use strict';

  function formatMoney(cents) {
    try {
      if (window.theme && window.theme.Currency && window.theme.settings) {
        return window.theme.Currency.formatMoney(cents, window.theme.settings.moneyFormat);
      }
    } catch (e) { /* 回退到默认格式 */ }
    return '$' + (Number(cents || 0) / 100).toFixed(2);
  }

  function cartAddUrl() {
    return (window.theme && window.theme.routes && window.theme.routes.cartAdd) || '/cart/add.js';
  }

  class FreeGiftUpsells extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('[data-fgu-toggle]').forEach((btn) => {
        btn.addEventListener('click', () => {
          if (btn.disabled) return;
          const input = btn.parentNode.querySelector('[data-fgu-input]');
          if (!input) return;
          input.checked = !input.checked;
          this.clearError();
          this.update();
        });
      });

      /* 变体（颜色）切换：把选中的 variant id / 价格同步回 checkbox 和行，
         再重算 FREE / 省了多少钱。真正加进购物车时读的就是 data-variant-id。 */
      this.querySelectorAll('[data-fgu-variant-select]').forEach((sel) => {
        sel.addEventListener('change', () => {
          const row = sel.closest('[data-fgu-row]');
          const input = row && row.querySelector('[data-fgu-input]');
          const opt = sel.options[sel.selectedIndex];
          if (!row || !input || !opt) return;

          const vid = opt.value;
          const price = opt.getAttribute('data-price') || '0';
          input.setAttribute('data-variant-id', vid);
          input.setAttribute('data-price', price);
          row.setAttribute('data-price', price);

          // 选中颜色的变体图，换色时缩略图跟着换（没有变体图就不动）
          const img = row.querySelector('[data-fgu-row-img]');
          if (img && opt.getAttribute('data-image')) {
            img.src = opt.getAttribute('data-image');
          }

          this.clearError();
          this.update();
        });
      });

      this.update();
    }

    get maxFree() {
      const n = parseInt(this.getAttribute('data-free-count') || '0', 10);
      return isNaN(n) || n < 0 ? 0 : n;
    }

    get isLocked() {
      return this.getAttribute('data-locked') === 'true';
    }

    /* 组件渲染在表单外面，靠 data-form-id 关联回商品表单。
       三级兜底：data-form-id → 同 section 范围内查 → 全局兜底。
       section_id 来自 section.id，每个 section 实例唯一，不会串台。 */
    get form() {
      const id = this.getAttribute('data-form-id');
      if (id) {
        const el = document.getElementById(id);
        if (el) return el;
      }
      const scope = this.closest('[data-section-id]') || document;
      const inScope = scope.querySelector('form[action*="/cart/add"]');
      if (inScope) return inScope;
      return this.closest('form') || document.querySelector('form[action*="/cart/add"]');
    }

    clearError() {
      this.classList.remove('is-invalid');
      const el = this.querySelector('[data-fgu-error]');
      if (el) el.textContent = '';
    }

    /* 展示阻断提示：滚动定位 + 红色描边 + 抖动 + 焦点落到第一个可勾行 */
    showMessage(text) {
      const el = this.querySelector('[data-fgu-error]');
      if (el) el.textContent = text || '';

      this.classList.remove('is-invalid');
      void this.offsetWidth; // 强制重排，保证动画可重复触发
      if (!text) return;

      this.classList.add('is-invalid');
      try {
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (e) {
        this.scrollIntoView();
      }
      const first = this.querySelector('[data-fgu-toggle]:not([disabled])');
      if (first) first.focus({ preventScroll: true });
    }

    /* remaining > 0：还差几件；remaining < 0：勾超了几件 */
    showError(remaining) {
      const max = this.maxFree;
      if (remaining > 0) {
        this.showMessage(
          remaining === 1
            ? 'Please pick 1 more free accessory to continue.'
            : 'Please pick ' + remaining + ' more free accessories to continue.'
        );
      } else {
        const over = -remaining;
        this.showMessage(
          'You can only pick ' + max + ' free accessor' + (max === 1 ? 'y' : 'ies') +
          '. Please uncheck ' + over + '.'
        );
      }
    }

    update() {
      const rows = Array.from(this.querySelectorAll('[data-fgu-row]'));
      const max = this.maxFree;
      const locked = this.isLocked;
      let checkedCount = 0;
      let savings = 0;

      rows.forEach((row) => {
        const input = row.querySelector('[data-fgu-input]');
        const btn = row.querySelector('[data-fgu-toggle]');
        const oldEl = row.querySelector('[data-fgu-old]');
        const newEl = row.querySelector('[data-fgu-new]');
        const sel = row.querySelector('[data-fgu-variant-select]');
        const price = parseInt(row.getAttribute('data-price') || '0', 10);
        const isChecked = !!(input && input.checked);

        if (btn) {
          btn.setAttribute('aria-pressed', isChecked ? 'true' : 'false');
          btn.classList.toggle('is-selected', isChecked);
        }
        // 选中态边框现在画在行容器上（卡片外观已从按钮移到 .fgu-row）
        row.classList.toggle('is-selected', isChecked);

        // 加价购行：不参与 N 件计数、不参与 FREE 显示、不受「选满锁住」影响。
        // 颜色下拉也不能受 locked 影响 —— 加价购行永远可勾，下拉就该一直可用。
        if (row.hasAttribute('data-fgu-paid')) {
          if (sel) sel.disabled = false;
          return;
        }

        if (isChecked) checkedCount += 1;
        const isFree = isChecked && (!max || checkedCount <= max);
        if (isFree) savings += price;

        if (oldEl) {
          if (isFree) {
            oldEl.textContent = formatMoney(price);
            oldEl.hidden = false;
          } else {
            oldEl.hidden = true;
          }
        }
        if (newEl) {
          newEl.textContent = isFree ? 'FREE' : formatMoney(price);
          newEl.classList.toggle('is-free', !!isFree);
        }
        if (btn) {
          btn.disabled = locked || (max > 0 && checkedCount >= max && !isChecked);
        }
        // 颜色下拉只在「这行没勾上、而且已经不能再勾」时禁用。
        // 锁定态的免费行按钮虽然 disabled，但它是强制勾选的，顾客仍要能挑颜色。
        if (sel) sel.disabled = !!(btn && btn.disabled && !isChecked);
      });

      const label = this.querySelector('[data-fgu-label]');
      if (label) {
        if (!max) {
          label.textContent = '';
        } else if (checkedCount >= max) {
          label.textContent = 'Order Now & Save ' + formatMoney(savings);
        } else if (locked) {
          // 锁定态下顾客补不了勾，绝不能提示 "Pick N More"（他做不到这件事）。
          // Liquid 的渲染守卫（可售行数 >= N）保证走不到这里，纯防御。
          label.textContent = '';
        } else {
          const remaining = max - checkedCount;
          if (remaining === max) {
            label.textContent = 'Pick ' + max + ' Free Accessories';
          } else if (remaining === 1) {
            label.textContent = 'Pick 1 More Free Accessory';
          } else {
            label.textContent = 'Pick ' + remaining + ' More Free Accessories';
          }
        }
      }

      // 勾满后自动清除阻断提示
      if (max > 0 && checkedCount >= max) this.clearError();

      this.dispatchEvent(new CustomEvent('fgu:change', { bubbles: true }));
    }

    /* 只显示加价购（addon_only）：该商品不送免费礼包。
       Liquid 侧已经不渲染任何赠品行，这里再兜一层——
       即使将来有人误加了赠品行，也绝不会被写进购物车/订单。 */
    get isAddonOnly() {
      return this.getAttribute('data-addon-only') === 'true';
    }

    /* 勾选中的免费赠品（不带 data-fgu-paid） */
    selectedItems() {
      if (this.isAddonOnly) return [];
      return this.collect('[data-fgu-input]:not([data-fgu-paid])', '_fgu_gift');
    }

    /* 勾选中的加价购配件（带 data-fgu-paid），原价，不参与免单 */
    selectedAddons() {
      return this.collect('[data-fgu-input][data-fgu-paid]', '_fgu_addon');
    }

    collect(selector, propName) {
      const parent = this.getAttribute('data-parent-handle') || '';
      return Array.from(this.querySelectorAll(selector))
        .filter((input) => input.checked)
        .map((input) => {
          const props = { _fgu_parent: parent };
          props[propName] = 'true';
          return {
            id: parseInt(input.getAttribute('data-variant-id'), 10),
            quantity: 1,
            properties: props
          };
        })
        .filter((item) => !!item.id);
    }

    addWithGifts(form, gifts, addons) {
      const btn = form.querySelector('[data-add-to-cart]');
      const cartAdd = cartAddUrl();
      const items = (gifts || []).concat(addons || []);

      // Buy Now 流程不在 Add to Cart 按钮上挂加载态（避免「点 A 按钮、B 按钮转圈」的困惑）
      if (btn && !window.__emojoBuyNowFlow) btn.classList.add('btn--loading');

      const done = () => {
        if (btn) btn.classList.remove('btn--loading');
        this.clearError();
        // 必须自己派发：theme.js 的 addItemFromForm 已被我们跳过
        document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
        this.dispatchEvent(new CustomEvent('fgu:added', { bubbles: true }));
      };
      const fail = (err) => {
        if (btn) btn.classList.remove('btn--loading');
        if (window.console) console.warn('[free-gift-upsells]', err && err.message);
        this.showMessage(
          (err && err.message) ? err.message : 'Unable to add to cart. Please try again.'
        );
      };
      const postJson = (body) =>
        fetch(cartAdd, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(body)
        })
          .then((res) => res.json())
          .then((data) => {
            if (data && data.status === 422) {
              throw new Error(data.description || data.message || 'Unable to add item');
            }
          });

      /* 主商品与赠品并进同一次 add.js。原先两段串行请求，第二段要等第一段
         整个往返结束才开始，手机上多花 2~3 秒；items[] 按数组顺序入车，
         主商品仍是第一行，结果与两段式一致。
         表单带 items[] 表达不了的字段（selling_plan / 自定义 line property）
         或读不到变体 id 时，退回原两段式，行为不变。 */
      /* Motion 的变体 id 是 <select name="id">，不能用 input[name="id"]，
         选择器一律不限标签 */
      const idInput = form.querySelector('[name="id"]');
      const mainId = idInput ? parseInt(idInput.value, 10) : NaN;
      const hasExtraFields = !!form.querySelector(
        '[name="selling_plan"], [name^="properties"]'
      );

      if (mainId && !hasExtraFields) {
        const qtyInput = form.querySelector('[name="quantity"]');
        const main = {
          id: mainId,
          quantity: qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1
        };
        postJson({ items: [main].concat(items) }).then(done).catch(fail);
        return;
      }

      fetch(cartAdd, {
        method: 'POST',
        body: new FormData(form),
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then((res) => res.json())
        .then((data) => {
          if (data && data.status === 422) {
            throw new Error(data.description || data.message || 'Unable to add item');
          }
          if (!items.length) return null;
          return postJson({ items: items });
        })
        .then(done)
        .catch(fail);
    }
  }

  if (window.customElements && !window.customElements.get('free-gift-upsells')) {
    window.customElements.define('free-gift-upsells', FreeGiftUpsells);
  }

  /* ---------------------------------------------------------------
     接管加购：capture 阶段拦在主题自己的 submit 监听之前。

     判定依据改成「form 自身的 action」而不是 evt.submitter[data-add-to-cart]：
       - 数量框按 Enter 提交时 evt.submitter === null
       - requestSubmit() 无参调用时也是 null
       - 主题升级后若删掉该属性会静默失效，最难排查
     动态结账按钮（Buy it now）是 type="button"，不触发 submit，天然不冲突。
     --------------------------------------------------------------- */
  function findWidget(form) {
    return Array.from(document.querySelectorAll('free-gift-upsells'))
      .find((w) => w.form === form) || null;
  }

  if (!window.__fguSubmitHooked) {
    window.__fguSubmitHooked = true;

    document.addEventListener('submit', (evt) => {
      const form = evt.target;
      if (!form || form.tagName !== 'FORM') return;
      if (!form.matches('form[action*="/cart/add"]')) return;

      const widget = findWidget(form);
      if (!widget) return;

      const max = widget.maxFree;
      const gifts = widget.selectedItems();
      const addons = widget.selectedAddons();

      // 既没有赠品要求、也没勾加价购 → 完全不干预主题原逻辑
      if (!max && !addons.length) return;

      // 抢断 theme.js:2772（form 上的 bubble 监听）
      evt.preventDefault();
      evt.stopImmediatePropagation();

      // 有赠品要求时必须恰好 N 件（加价购不占这 N 件的名额）
      if (max) {
        const remaining = max - gifts.length;
        if (remaining !== 0) {
          widget.showError(remaining); // 硬阻断
          return;
        }
      }

      if (!gifts.length && !addons.length) return;

      widget.addWithGifts(form, gifts, addons);
    }, true);
  }
})();
